import { useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";
import type { FieldOption } from "../../lib/field-util";

// Multi-select with chips + dropdown checkbox list + optional inline search.
// Matches the discovery_app SearchableMultiSelectRow shape.

interface MultiSelectProps {
  options: readonly FieldOption[];
  selected: readonly string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
  searchable?: boolean;
  className?: string;
}

export function MultiSelect({
  options,
  selected,
  onChange,
  placeholder,
  searchable = false,
  className = "",
}: MultiSelectProps) {
  const intl = useIntl();
  const ph = placeholder ?? intl.formatMessage({ id: "design.selectPlaceholder", defaultMessage: "Select …" });
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && e.target instanceof Node && !ref.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const selectedSet = new Set(selected);
  const labelFor = (v: string) => options.find((o) => o.value === v)?.label ?? v;
  const visible =
    searchable && query.trim()
      ? options.filter((o) => o.label.toLowerCase().includes(query.trim().toLowerCase()))
      : options;

  const toggle = (value: string) => {
    if (selectedSet.has(value)) onChange(selected.filter((v) => v !== value));
    else onChange([...selected, value]);
  };

  return (
    <div ref={ref} className={`relative ${className}`}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex min-h-[34px] w-full flex-wrap items-center gap-1 rounded bg-surface px-2 py-1 text-left ring-1 ring-ink/10 focus:outline-none focus:ring-1 focus:ring-accent"
      >
        {selected.length === 0 ? (
          <span className="text-[13px] text-ink/45">{ph}</span>
        ) : (
          selected.map((v) => (
            <span
              key={v}
              className="inline-flex items-center gap-1 rounded-full bg-ink/[0.06] px-2 py-0.5 text-[11px] text-ink/80"
            >
              {labelFor(v)}
              <span
                role="button"
                tabIndex={-1}
                onClick={(e) => {
                  e.stopPropagation();
                  toggle(v);
                }}
                className="cursor-pointer text-ink/40 hover:text-ink"
              >
                × {/* i18n-allow */}
              </span>
            </span>
          ))
        )}
      </button>

      {open ? (
        <div className="absolute left-0 right-0 z-50 mt-1 max-h-64 overflow-auto rounded-md bg-surface shadow-lg ring-1 ring-ink/10">
          {searchable ? (
            <div className="sticky top-0 bg-surface p-2">
              <input
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={intl.formatMessage({ id: "design.filterPlaceholder", defaultMessage: "Type to filter …" })}
                className="w-full rounded bg-surface px-2 py-1 text-[13px] text-ink ring-1 ring-ink/10 placeholder:text-ink/40 focus:outline-none focus:ring-ink/30"
              />
            </div>
          ) : null}
          {visible.length === 0 ? (
            <div className="px-3 py-2 text-[12px] text-ink/45">{intl.formatMessage({ id: "search.noResults", defaultMessage: "No matches" })}</div>
          ) : (
            renderGrouped(visible, selectedSet, toggle)
          )}
        </div>
      ) : null}
    </div>
  );
}

function renderGrouped(
  options: readonly FieldOption[],
  selectedSet: Set<string>,
  toggle: (v: string) => void,
) {
  const hasSections = options.some((o) => o.section);
  if (!hasSections) {
    return options.map((o) => renderOption(o, selectedSet, toggle));
  }
  const order: string[] = [];
  const grouped = new Map<string, FieldOption[]>();
  for (const o of options) {
    const key = o.section ?? "";
    if (!grouped.has(key)) {
      grouped.set(key, []);
      order.push(key);
    }
    grouped.get(key)!.push(o);
  }
  return order.map((section) => (
    <div key={section || "__none__"}>
      {section ? (
        <div className="sticky top-0 bg-surface px-3 pb-1 pt-2 text-[9px] uppercase tracking-[0.2em] text-ink/40">
          {section}
        </div>
      ) : null}
      {grouped.get(section)!.map((o) => renderOption(o, selectedSet, toggle))}
    </div>
  ));
}

function renderOption(
  o: FieldOption,
  selectedSet: Set<string>,
  toggle: (v: string) => void,
) {
  return (
    <label
      key={o.value}
      className="flex cursor-pointer items-center gap-2 px-3 py-1.5 text-[13px] text-ink/80 hover:bg-ink/[0.06]"
    >
      <input
        type="checkbox"
        checked={selectedSet.has(o.value)}
        onChange={() => toggle(o.value)}
        className="accent-accent"
      />
      <span className="truncate">
        {o.glyph ? <span className="mr-1 text-ink/55">{o.glyph}</span> : null}
        {o.label}
      </span>
    </label>
  );
}
